import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { RootLayout } from '@/components/layout/RootLayout'
import { StatsGrid } from '@/components/dashboard/StatsGrid'
import { AgentCard } from '@/components/dashboard/AgentCard'
import { UsageChart } from '@/components/dashboard/UsageChart'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassButton } from '@/components/ui/GlassButton'
import { agentsQueryOptions, usageSummaryQueryOptions } from '@/lib/queryOptions'
import { Link } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useT } from '@/i18n/provider'

export const Route = createFileRoute('/dashboard/')({
  beforeLoad: async ({ context: { queryClient } }) => {
    // Pre-fetch data
    await Promise.all([
      queryClient.ensureQueryData(agentsQueryOptions()),
      queryClient.ensureQueryData(usageSummaryQueryOptions(30)),
    ]).catch(() => {
      throw redirect({ to: '/auth/login' })
    })
  },
  component: DashboardPage,
})

function DashboardPage() {
  const t = useT()
  const { data: agents = [] } = useQuery(agentsQueryOptions())
  const { data: usage } = useQuery(usageSummaryQueryOptions(30))

  const recentAgents = agents.slice(0, 6)

  return (
    <RootLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">{t('dashboard.home.title')}</h1>
            <p className="text-sm text-white/40 mt-1">{t('dashboard.home.subtitle')}</p>
          </div>
          <Link to="/dashboard/agents">
            <GlassButton size="sm" variant="primary">
              <Plus size={14} />
              {t('dashboard.home.newAgent')}
            </GlassButton>
          </Link>
        </div>

        {/* Stats */}
        <StatsGrid />

        {/* Agents + Usage side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Agents — 2 cols */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white/70">{t('dashboard.home.recentAgents')}</h2>
              <Link to="/dashboard/agents" className="text-xs text-[var(--oc-accent-light)] hover:underline">
                {t('dashboard.home.viewAll')}
              </Link>
            </div>
            {recentAgents.length === 0 ? (
              <GlassCard className="text-center py-12 space-y-3">
                <p className="text-white/40 text-sm">{t('dashboard.home.noAgents')}</p>
                <Link to="/dashboard/agents">
                  <GlassButton size="sm">{t('dashboard.home.createFirstAgent')}</GlassButton>
                </Link>
              </GlassCard>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {recentAgents.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} />
                ))}
              </div>
            )}
          </div>

          {/* Usage chart — 1 col */}
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-white/70">{t('dashboard.home.tokenUsage30d')}</h2>
            <GlassCard>
              {usage ? (
                <UsageChart data={usage} />
              ) : (
                <div className="h-28 flex items-center justify-center text-white/30 text-sm">
                  {t('common.loading')}
                </div>
              )}
            </GlassCard>
          </div>
        </div>
      </div>
    </RootLayout>
  )
}
